/**
 * Waiting for the voice a RUN owns, and asking for the one spoken at play time — CCT-310.
 *
 * TWO NEEDS, ONE PLACE TO HOLD THEM
 * ---------------------------------
 * A scene may carry two kinds of line the author could not voice in advance:
 *
 *   * `instance` — they name the player. They are rendered IN THE BACKGROUND when the run
 *     starts. Until that render finishes the scene WAITS: half the lines voiced and the other
 *     half silent does not read as loading, it reads as broken.
 *   * `runtime` — they state a status that moves. They are asked for AT THE MOMENT of playing.
 *
 * ⚠️ NO WAIT IS UNBOUNDED, AND THE BOUND IS A TIMER THAT RACES THE REQUEST. The server counts a
 * render stopped by the cap as finished, and any network error releases the scene. A mission
 * frozen on a spinner is worse than a mission read on screen: the sound is an enrichment, not a
 * condition.
 *
 * The waiting itself lives in `voixBornee`, free of React, so the bounds can be RUN in a test
 * rather than recognised in the source. These hooks only bind it to the API and to a state.
 */
import { useEffect, useState } from 'react';

import { fetchLineAudio, readRunAudio } from '../api/runAudio';
import { attendreVoixPrete, demanderCleLigne } from './voixBornee';

/**
 * Blocks until the run's voice is ready. Returns `true` when the scene may play.
 *
 * @param actif      does the scene hold personalised lines not yet ready?
 */
export function useRunAudioReady(
  token: string | null, missionId: number | undefined, actif: boolean,
): boolean {
  const [pret, setPret] = useState(!actif);

  useEffect(() => {
    if (!actif || !missionId) { setPret(true); return; }
    return attendreVoixPrete(() => readRunAudio(token, missionId), setPret);
  }, [token, missionId, actif]);

  return pret;
}

/**
 * Audio key of a `runtime` line, asked for at the moment of playing it.
 *
 * ⚠️ NO CLIENT-SIDE CACHE. The SERVER knows whether the status moved: it keeps the audio under
 * the fingerprint of the resolved text, so a replay costs it nothing. A local cache would replay
 * a sentence that has become false — precisely what the `runtime` kind exists to prevent.
 *
 * Returns `undefined` while we do not know, and `''` when the line will not be voiced. The two
 * are handled differently: one makes the beat wait a moment, the other makes it play silent.
 */
export function useStreamedLineAudio(
  token: string | null, missionId: number | undefined,
  itemId: string | undefined, path: string | undefined, actif: boolean,
): string | undefined {
  const [cle, setCle] = useState<string | undefined>(actif ? undefined : '');

  useEffect(() => {
    if (!actif || !missionId || !itemId || !path) { setCle(''); return; }
    setCle(undefined);
    return demanderCleLigne(
      () => fetchLineAudio(token, missionId, itemId, path), setCle);
  }, [token, missionId, itemId, path, actif]);

  return cle;
}
